import { useCallback, useEffect, useMemo, useState } from 'react';
import * as L from 'leaflet';
import {
  getStatusEngine,
  getVesselStatus,
  renderMarkup,
  VesselStatus,
} from '../../../helpers/vessel';
import {
  CircleMarker,
  Polyline,
  Popup,
  Tooltip,
  useMapEvents,
} from 'react-leaflet';
import MapVesselPopup from './MapVesselPopup';
import useMapSetting from '../../../zustand/useMap';
import { LastDataLogProps } from '../HomeCustom';
import RotatedMarker from './RotatedMarker';
import { IRpm } from '../../report/types/report-log.type';
import CoordinatePopup from './CoordinatePopup';
import useGroup from '../../../zustand/useGroup';
import { toHHMM } from '../../report/components/helperFunction';
import MapTooltip from './MapTooltip';

type HomeCustomMarkerProps = {
  lastDataLog: LastDataLogProps;
  showVesselOffline: boolean;
  showVesselOnline: boolean;
  showVesselWaiting: boolean;
  showVesselDotTrail: boolean;
  showVesselLabel: boolean;
  trailWidth: number;
  trailOpacity: number;
  zoomLevel: number;
};

const MapMarkers: React.FC<HomeCustomMarkerProps> = ({
  lastDataLog,
  showVesselOffline,
  showVesselOnline,
  showVesselWaiting,
  showVesselDotTrail,
  showVesselLabel,
  trailWidth,
  trailOpacity,
  zoomLevel,
}) => {
  const { filter, timezone } = useGroup();
  const { dataSource } = useMapSetting();
  const [zoom, setZoom] = useState<number>(zoomLevel | 8);
  const [dataMarkers, setDataMarkers] = useState<any[]>([]);

  const map = useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
    },
  });

  useEffect(() => {
    const currentDataMarkers: any[] = [];

    if (lastDataLog) {
      Object.keys(lastDataLog).forEach((assetName) => {
        let gps: any = {};
        let rpm: any = {};
        let lastDataGPS: any = {};
        let lastDataRPM: any = {};
        let polyline: any = [];
        let newCoords: any = [];

        if (lastDataLog[assetName]) {
          if (lastDataLog[assetName].gps) {
            gps = lastDataLog[assetName].gps;
          }

          if (lastDataLog[assetName].rpm) {
            rpm = lastDataLog[assetName].rpm;
          }

          // console.log('gps rpm', { gps, rpm });

          if (gps) {
            if (Object.keys(gps).length === 1) {
              lastDataGPS = Object.values(gps)[0];
            } else if (Object.keys(gps).length > 1) {
              lastDataGPS = Object.values(gps)[Object.keys(gps).length - 1];
            }
          }

          lastDataRPM =
            rpm && Object.keys(rpm).length > 0 && Object.values(rpm)[0];

          const status = getVesselStatus(lastDataGPS, dataSource, filter, assetName);
          let isEngineOn = false;

          if (rpm) {
            isEngineOn = getStatusEngine(rpm, dataSource, {
              ...filter,
              timezone,
            });
          }

          const gpsKeys = gps ? Object.keys(gps) : [];

          if (gpsKeys.length >= 1) {
            const gpsValues = Object.values(gps);
            lastDataGPS = gpsValues[gpsValues.length - 1];
            const gpsLength = gpsKeys.length;
            const reductionFactor = Math.ceil(
              gpsLength / Math.pow(2, zoom - 4)
            );

            gpsKeys.forEach((key, idx) => {
              if (
                (gps as any)[key].latitude &&
                (gps as any)[key].longitude &&
                (gps as any)[key].latitude !== 0 &&
                (gps as any)[key].longitude !== 0
              ) {
                polyline.push({
                  lat: (gps as any)[key].latitude,
                  lng: (gps as any)[key].longitude,
                });
              }

              const timestamp = gpsKeys[idx > 0 ? idx * reductionFactor : 0];

              if (
                gps[timestamp] &&
                gps[timestamp].latitude !== 0 &&
                gps[timestamp].longitude
              ) {
                newCoords.push({
                  coords: {
                    lat: gps[timestamp].latitude,
                    lng: gps[timestamp].longitude,
                  },
                  speed: gps[timestamp].speed,
                  heading: gps[timestamp].heading,
                  altitude: gps[timestamp].altitude,
                  timestamp: gps[timestamp].timestamp,
                  distance: gps[timestamp].distance,
                  assetName,
                });
              }
            });
          }

          let rpmPort: any = null;
          let rpmStarboard: any = null;
          let rpmCenter: any = null;

          if (lastDataRPM.port) {
            rpmPort = {
              rpm: lastDataRPM.port.rpm,
              timestamp: lastDataRPM.port.timestamp,
              // runningTime:
              //   lastDataRPM.port && lastDataRPM.port.runningTime
              //     ? toHHMM(lastDataRPM.port.runningTime)
              //     : '-',
            };
          }

          if (lastDataRPM.starboard) {
            rpmStarboard = {
              rpm: lastDataRPM.starboard.rpm,
              timestamp: lastDataRPM.starboard.timestamp,
              // runningTime:
              //   lastDataRPM.starboard && lastDataRPM.starboard.runningTime
              //     ? toHHMM(lastDataRPM.starboard.runningTime)
              //     : '-',
            };
          }

          if (lastDataRPM.center) {
            rpmCenter = {
              rpm: lastDataRPM.center.rpm,
              timestamp: lastDataRPM.center.timestamp,
              // runningTime:
              //   lastDataRPM.center && lastDataRPM.center.runningTime
              //     ? toHHMM(lastDataRPM.center.runningTime)
              //     : '-',
            };
          }

          currentDataMarkers.push({
            ...lastDataGPS,
            assetId: lastDataLog[assetName].assetId,
            name: assetName,
            status,
            isEngineOn,
            polyline,
            rpmPort,
            rpmStarboard,
            rpmCenter,
            // ae1: {
            //   fuelCons: lastDataAE1.ae1.fuelConsumption,
            //   runningTime: lastDataAE1.ae1.runningTime,
            // },
            // ae2: {
            //   fuelCons: lastDataAE2.ae2.fuelConsumption,
            //   runningTime: lastDataAE2.ae2.runningTime,
            // },
            // ae3: {
            //   fuelCons: lastDataAE3.ae3.fuelConsumption,
            //   runningTime: lastDataAE3.ae3.runningTime,
            // },
            gps,
            coords: newCoords,
          });
        }
      });
    }

    setDataMarkers(currentDataMarkers);
  }, [lastDataLog, zoom]);

  const renderIcon = (data: {
    assetName: string;
    vesselStatus: VesselStatus;
    showVesselOnline: boolean;
    showVesselOffline: boolean;
    showVesselWaiting: boolean;
  }) => {
    return new L.DivIcon({
      html: renderMarkup(data),
      iconSize: [60, 60],
      iconAnchor: [30, 30],
    });
  };

  // console.log('dataMarkers', { dataMarkers });

  return (
    <>
      {dataMarkers.length > 0 &&
        dataMarkers
          .filter((item) => item.latitude && item.longitude)
          .map((data, index) => (
            <div key={`${data.assetId}-${data.timestamp}`}>
              <RotatedMarker
                key={`${data.assetId}-${data.timestamp}`}
                rotationOrigin='center'
                rotationAngle={data.heading || 0}
                position={{
                  lat: data.latitude,
                  lng: data.longitude,
                }}
                icon={renderIcon({
                  assetName: data.name,
                  vesselStatus: data.status,
                  showVesselOnline,
                  showVesselOffline,
                  showVesselWaiting,
                })}
                onClick={() => {
                  // handleClickMarker(item)
                }}
              >
                <Popup
                  offset={[0, -15]}
                  className='custom-popup custom-vessel-popup'
                >
                  <MapVesselPopup
                    assetId={data.assetId}
                    timestamp={data.timestamp}
                    latitude={data.latitude}
                    longitude={data.longitude}
                    altitude={data.altitude}
                    speed={String(data.speed)}
                    vesselStatus={data.status}
                    rpmPort={data.rpmPort}
                    rpmStarboard={data.rpmStarboard}
                    rpmCenter={data.rpmCenter}
                    ae1={data.ae1}
                    ae2={data.ae2}
                    ae3={data.ae3}
                    rpm={data.rpm}
                    isEngineOn={data.isEngineOn}
                  />
                </Popup>
                <MapTooltip
                  key={`${data.assetId}-${data.timestamp}`}
                  dataStatus={data.status}
                  name={data.name}
                  showVesselLabel={showVesselLabel}
                />
              </RotatedMarker>

              {showVesselDotTrail &&
                data &&
                data.polyline &&
                data.polyline.length > 5 && (
                  <Polyline
                    // smoothFactor={2}
                    pathOptions={{
                      color:
                        data.status === VesselStatus.ONLINE
                          ? '#55C95A'
                          : data.status === VesselStatus.OFFLINE
                          ? '#FE4545'
                          : '#A4660A',
                      weight: trailWidth,
                      fillOpacity: trailOpacity,
                      opacity: trailOpacity,
                    }}
                    positions={data.polyline}
                  />
                )}

              {showVesselDotTrail &&
                data.coords.length > 1 &&
                data.coords.map((item: any, idx: number) => (
                  <CircleMarker
                    key={idx}
                    center={item.coords}
                    radius={5}
                    fill
                    fillOpacity={1}
                    fillColor={'#ffffff'}
                    pathOptions={{
                      color:
                        data.status === VesselStatus.ONLINE
                          ? '#55C95A'
                          : data.status === VesselStatus.OFFLINE
                          ? '#FE4545'
                          : '#A4660A',
                    }}
                  >
                    <Popup offset={[0, -15]} className='custom-popup-info'>
                      <CoordinatePopup
                        speed={item.speed}
                        asset={item.assetName}
                        longitude={item.coords.lat}
                        latitude={item.coords.lng}
                        altitude={item.altitude}
                        heading={item.heading}
                        timestamp={item.timestamp}
                        distance={item.distance}
                      />
                    </Popup>
                  </CircleMarker>
                ))}
            </div>
          ))}
    </>
  );
};

export default MapMarkers;
